import { format, isValid, parse } from "date-fns";
import type { useIntl } from "react-intl";

import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { StopAddressType, StopTimeType } from "types/MultiStop";
import { stopDateTimeToDate } from "@RequestFormV2/sections/Schedule/utils/scheduleDateTime";

export function formatTimezoneForReview(label: string): string {
  return label.replace(/\s\(UTC\s([^)]+)\)/, " UTC $1");
}

export function formatReviewDeadline(date?: string, time?: string, timezoneLabel?: string): string | undefined {
  if (!date) return undefined;
  const combined = time ? `${date} ${time}` : date;
  const parseFormat = time?.length === 8 ? "yyyy-MM-dd HH:mm:ss" : time ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd";
  const parsed = parse(combined, parseFormat, new Date());
  const formatted = !isValid(parsed)
    ? combined
    : time
      ? format(parsed, "d MMM yyyy, HH:mm")
      : format(parsed, "d MMM yyyy");
  if (!timezoneLabel) return formatted;
  const timezone = formatTimezoneForReview(timezoneLabel);
  return timezone ? `${formatted} (${timezone})` : formatted;
}

export function formatDateTime(date?: string | null, time?: string | null): string {
  const parsed = stopDateTimeToDate(date, time);
  if (!parsed) return "";
  return format(parsed, "d MMM yyyy, HH:mm");
}

export function formatHoursTime(time: string): string {
  return time.length >= 5 ? time.slice(0, 5) : time;
}

export function formatStopDate(
  stop: ScheduleStopValue,
  intl: ReturnType<typeof useIntl>,
  getTimezoneLabel: (value?: string | null) => string,
): string {
  const { stop_time_type, stop_after_date, stop_after_time, stop_before_date, stop_before_time, stop_timezone } = stop;
  const tzLabel = getTimezoneLabel(stop_timezone);
  const tz = tzLabel ? ` (${formatTimezoneForReview(tzLabel)})` : "";

  if (stop_time_type === StopTimeType.ASAP_DELIVERY) {
    return intl.formatMessage({ id: "request-form.review.schedule.asap", defaultMessage: "ASAP" });
  }

  if (stop_time_type === StopTimeType.AFTER) {
    const dateTime = formatDateTime(stop_after_date, stop_after_time);
    return `${intl.formatMessage({
      id: "request-form.review.schedule.after",
      defaultMessage: "After",
    })} ${dateTime}${tz}`;
  }

  if (stop_time_type === StopTimeType.BEFORE) {
    const dateTime = formatDateTime(stop_before_date, stop_before_time);
    return `${intl.formatMessage({
      id: "request-form.review.schedule.before",
      defaultMessage: "Before",
    })} ${dateTime}${tz}`;
  }

  if (stop_time_type === StopTimeType.BETWEEN) {
    const afterPart = formatDateTime(stop_after_date, stop_after_time);
    const beforePart = formatDateTime(stop_before_date, stop_before_time);
    return `${afterPart} - ${beforePart}${tz}`;
  }

  return "";
}

/**
 * The single representative datetime for a stop, shown as the primary line:
 * the ASAP label for ASAP delivery, the before-date for BEFORE stops,
 * both dates for BETWEEN stops, otherwise the after-date.
 */
export function getStopPrimaryDateTime(stop: ScheduleStopValue, intl: ReturnType<typeof useIntl>): string {
  if (stop.stop_time_type === StopTimeType.ASAP_DELIVERY) {
    return intl.formatMessage({ id: "request-form.review.schedule.asap", defaultMessage: "ASAP" });
  }
  if (stop.stop_time_type === StopTimeType.BEFORE) {
    return formatDateTime(stop.stop_before_date, stop.stop_before_time);
  }
  if (stop.stop_time_type === StopTimeType.BETWEEN) {
    const afterPart = formatDateTime(stop.stop_after_date, stop.stop_after_time);
    const beforePart = formatDateTime(stop.stop_before_date, stop.stop_before_time);
    if (afterPart && beforePart) return `${afterPart} - ${beforePart}`;
    return afterPart || beforePart;
  }
  return formatDateTime(stop.stop_after_date, stop.stop_after_time);
}

export function formatStreetAddress(stop: ScheduleStopValue): string {
  const parts: string[] = [];
  if (stop.street_address) parts.push(stop.street_address);
  if (stop.street_address_line_two) parts.push(stop.street_address_line_two);
  if (stop.city_name) parts.push(stop.city_name);
  if (stop.state_name) parts.push(stop.state_name);
  if (stop.zipcode) parts.push(stop.zipcode);
  if (stop.country_name) parts.push(stop.country_name);
  return parts.join(", ");
}

export function formatContactLine(phone?: string | null, email?: string | null): string {
  return [phone, email].filter(Boolean).join(" | ");
}

export function formatStopContactSummary(stop: ScheduleStopValue): string {
  return [stop.stop_contact_name, stop.stop_contact_number, stop.stop_contact_email].filter(Boolean).join(" | ");
}

export function formatPortCodeAndName(code?: string | null, name?: string | null): string {
  const trimmedCode = (code ?? "").trim();
  const trimmedName = (name ?? "").trim();

  if (!trimmedCode && !trimmedName) return "";
  if (!trimmedCode) return trimmedName;
  if (!trimmedName) return trimmedCode;
  if (trimmedCode === trimmedName) return trimmedCode;

  return `${trimmedCode} - ${trimmedName}`;
}

export function getStopPortLabel(stop: ScheduleStopValue): string {
  if (stop.stop_address_type === StopAddressType.AIRPORT) {
    return formatPortCodeAndName(stop.stop_airport_code, stop.stop_airport_name);
  }
  if (stop.stop_address_type === StopAddressType.SEAPORT) {
    return formatPortCodeAndName(stop.stop_seaport_code, stop.stop_seaport_name);
  }
  return "";
}

export function hasStopData(stop: ScheduleStopValue): boolean {
  return !!(
    stop.address_name ||
    stop.city_name ||
    stop.country_name ||
    stop.stop_airport_name ||
    stop.stop_seaport_name ||
    stop.street_address
  );
}
